import { useMutation, useQuery } from "@apollo/client/react";
import {
  MyAvatarDocument,
  MyselfDocument,
  PersonProfileDocument,
  UpdatePersonDocument,
  type MyselfQuery,
  type PersonProfileQuery,
  type UpdatePersonDataInput,
} from "@/graphql/generated/operations";

// Person-aggregate hook.
//
// Single-entity, batched partial updates (the Person aggregate from
// ADR-0004 - scalars + sharedAttributes + avatar). One UpdatePerson call
// per commit, accepting the codegen UpdatePersonDataInput shape directly
// so the caller can build it from whatever overlay machinery it uses
// (e.g. ProfilePage's SaveBar batches edits and emits this on save).
//
// Differs from useMatchEditor (immediate-per-field autosave) and the
// 5 entry hooks (list CRUD): one logical write per commit, no debounce,
// no autosave. The "when to call" question is owned by the caller.

export type PersonForEdit =
  | NonNullable<MyselfQuery["myself"]>
  | NonNullable<PersonProfileQuery["person"]>;

export type AvatarFile = {
  id?: string | null;
  mimeType?: string | null;
  content?: string | null;
};

export interface UsePersonResult {
  person: PersonForEdit | null;
  viewerId: string | null;
  isOwn: boolean;
  avatar: AvatarFile | null;
  loading: boolean;
  notFound: boolean;
  error: Error | null;
  busy: boolean;
  refetch: () => Promise<unknown>;
  refetchAvatar: () => Promise<unknown>;
  update: (data: UpdatePersonDataInput) => Promise<void>;
}

export function usePerson(maybeOtherId?: string | null): UsePersonResult {
  const viewingOther = Boolean(maybeOtherId);

  // Both MyselfDocument and PersonProfileDocument are normalized to Person
  // by id in the Apollo cache; calls dedupe across consumers.
  const ownQuery = useQuery(MyselfDocument);
  const otherQuery = useQuery(PersonProfileDocument, {
    variables: { id: maybeOtherId ?? "" },
    skip: !viewingOther,
  });
  const avatarQuery = useQuery(MyAvatarDocument, { skip: viewingOther });

  const [updateMut, updateState] = useMutation(UpdatePersonDocument);

  const myself = ownQuery.data?.myself ?? null;
  const otherPerson = otherQuery.data?.person ?? null;
  const person: PersonForEdit | null = viewingOther ? otherPerson : myself;

  const avatar: AvatarFile | null = viewingOther
    ? (otherPerson?.avatar ?? null)
    : (avatarQuery.data?.myAvatar ?? null);

  async function update(data: UpdatePersonDataInput) {
    if (!person) throw new Error("usePerson.update: no person loaded");
    await updateMut({
      variables: { id: person.id, input: { data, dryRun: false } },
    });
    // UpdatePerson's response only carries { id, name } — Apollo's
    // normalized cache merge therefore touches just those two fields
    // and leaves the ~50 others (bio, city, sharedAttributes, …) at
    // their pre-mutation cached values. Refetch the driving query so
    // the page doesn't snap back to stale data when the SaveBar clears
    // its overlay. Avatar lives in a separate query and is refetched
    // by the avatar mutation path.
    if (viewingOther) {
      await otherQuery.refetch();
    } else {
      await ownQuery.refetch();
    }
  }

  return {
    person,
    viewerId: myself?.id ?? null,
    isOwn: !viewingOther || (myself?.id != null && person?.id === myself.id),
    avatar,
    loading:
      (viewingOther ? otherQuery.loading : ownQuery.loading) &&
      !(viewingOther ? otherQuery.data : ownQuery.data),
    notFound: viewingOther
      ? !otherQuery.loading && !otherPerson
      : !ownQuery.loading && !myself,
    error:
      (ownQuery.error as Error | undefined) ??
      (otherQuery.error as Error | undefined) ??
      (avatarQuery.error as Error | undefined) ??
      (updateState.error as Error | undefined) ??
      null,
    busy: updateState.loading,
    refetch: () => (viewingOther ? otherQuery.refetch() : ownQuery.refetch()),
    refetchAvatar: () => avatarQuery.refetch(),
    update,
  };
}
